/**
 * PDF Export Utility
 * Generates PDF reports for comp comparison data
 */

import jsPDF from 'jspdf';

/**
 * Export comp report to PDF
 * @param {Object} reportData - Report data from compReportService
 * @param {string} primaryFilmTitle - Title of primary film
 * @param {Array} compFilms - Array of comp film objects
 * @param {string} reportType - 'basic' or 'detailed'
 */
export const exportReportToPDF = (reportData, primaryFilmTitle, compFilms, reportType = 'basic') => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Helper to add a new page if needed
    const checkNewPage = (requiredHeight) => {
      if (yPosition + requiredHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Add header with Greenlight branding
    doc.setFillColor(34, 139, 34); // Green color
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Greenlight Analytics', margin, 25);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Film Comp Comparison Report', margin, 35);

    yPosition = 50;

    // Report title
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`${primaryFilmTitle} - Comp Report`, margin, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const reportDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(`Generated: ${reportDate}`, margin, yPosition);
    yPosition += 15;

    // Comp films list
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Comparison Films:', margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    compFilms.forEach((comp, index) => {
      doc.text(`${index + 1}. ${comp.compFilmTitle || comp.compFilmId}`, margin + 5, yPosition);
      yPosition += 6;
    });
    yPosition += 10;

    if (!reportData || !reportData.weeks || reportData.weeks.length === 0) {
      doc.text('No data available for comparison.', margin, yPosition);
      doc.save(`${primaryFilmTitle.replace(/[^a-z0-9]/gi, '_')}_Comp_Report.pdf`);
      return;
    }

    const { primaryFilm, compFilms: comps, weeks } = reportData;

    // Define metrics based on report type
    const metrics = reportType === 'basic' 
      ? [
          { key: 'pctAware', label: 'Awareness', unit: '%' },
          { key: 'pctInterest', label: 'Interest', unit: '%' },
          { key: 'pctTheater', label: 'Theater Intent', unit: '%' },
          { key: 'pctWillingToPay', label: 'Willing to Pay', unit: '%' },
          { key: 'pctHeat', label: 'Heat', unit: '%' },
          { key: 'pctPLF', label: 'PLF', unit: '%' }
        ]
      : [
          { key: 'pctAware', label: 'Awareness', unit: '%' },
          { key: 'pctInterest', label: 'Interest', unit: '%' },
          { key: 'pctInterestOfAware', label: 'Interest of Aware', unit: '%' },
          { key: 'pctInterestNotAware', label: 'Interest Not Aware', unit: '%' },
          { key: 'pctTheater', label: 'Theater Intent', unit: '%' },
          { key: 'pctWillingToPay', label: 'Willing to Pay', unit: '%' },
          { key: 'pctHeat', label: 'Heat', unit: '%' },
          { key: 'pctPLF', label: 'PLF', unit: '%' }
        ];

    // Helper to get metric value
    const getMetricValue = (filmData, week, metricKey) => {
      if (!filmData || !filmData.dataByWeeks) return 'N/A';
      const weekData = filmData.dataByWeeks[week];
      if (!weekData) return 'N/A';
      const value = weekData[metricKey];
      return (value === null || value === undefined) ? 'N/A' : value;
    };

    // Generate tables for each metric
    metrics.forEach((metric, metricIndex) => {
      checkNewPage(60);

      // Metric title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(metric.label, margin, yPosition);
      yPosition += 10;

      // Table header
      const colWidths = [30, ...comps.map(() => (contentWidth - 30) / (comps.length + 1))];
      let xPosition = margin;

      // Header row
      doc.setFillColor(200, 200, 200);
      doc.rect(xPosition, yPosition - 5, colWidths[0], 8, 'F');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Weeks', xPosition + 2, yPosition);
      xPosition += colWidths[0];

      // Primary film header
      doc.setFillColor(34, 139, 34);
      doc.rect(xPosition, yPosition - 5, colWidths[1], 8, 'F');
      doc.setTextColor(255, 255, 255);
      const primaryTitle = primaryFilmTitle.length > 20 
        ? primaryFilmTitle.substring(0, 17) + '...'
        : primaryFilmTitle;
      doc.text(primaryTitle, xPosition + 2, yPosition);
      xPosition += colWidths[1];

      // Comp film headers
      comps.forEach((comp, compIndex) => {
        doc.setFillColor(200, 200, 200);
        doc.rect(xPosition, yPosition - 5, colWidths[compIndex + 2], 8, 'F');
        doc.setTextColor(0, 0, 0);
        const compTitle = (comp.filmTitle || comp.compFilmId).length > 20
          ? (comp.filmTitle || comp.compFilmId).substring(0, 17) + '...'
          : (comp.filmTitle || comp.compFilmId);
        doc.text(compTitle, xPosition + 2, yPosition);
        xPosition += colWidths[compIndex + 2];
      });

      yPosition += 10;

      // Data rows
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      weeks.forEach((week) => {
        checkNewPage(8);

        xPosition = margin;
        
        // Week column
        doc.text(`${week}`, xPosition + 2, yPosition);
        xPosition += colWidths[0];

        // Primary film value
        const primaryValue = getMetricValue(primaryFilm, week, metric.key);
        doc.setFillColor(220, 255, 220);
        doc.rect(xPosition, yPosition - 4, colWidths[1], 6, 'F');
        doc.setTextColor(0, 0, 0);
        const primaryText = primaryValue === 'N/A' ? 'N/A' : `${primaryValue}${metric.unit}`;
        doc.text(primaryText, xPosition + 2, yPosition);
        xPosition += colWidths[1];

        // Comp film values
        comps.forEach((comp, compIndex) => {
          const compValue = getMetricValue(comp, week, metric.key);
          const compText = compValue === 'N/A' ? 'N/A' : `${compValue}${metric.unit}`;
          doc.text(compText, xPosition + 2, yPosition);
          xPosition += colWidths[compIndex + 2];
        });

        yPosition += 8;
      });

      yPosition += 10;
    });

    // Save PDF
    const fileName = `${primaryFilmTitle.replace(/[^a-z0-9]/gi, '_')}_Comp_Report_${reportType}.pdf`;
    doc.save(fileName);

    console.log('✅ PDF exported successfully:', fileName);
  } catch (error) {
    console.error('❌ Failed to export PDF:', error);
    alert('Failed to export PDF. Please try again.');
  }
};

export default {
  exportReportToPDF
};

